import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEOMeta, { SchemaOrg } from "@/components/feature/SEOMeta";
import Breadcrumb, { getArticleBreadcrumb } from "@/components/feature/Breadcrumb";
import { articles, categories } from "@/mocks/data";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const category = article ? categories.find((c) => c.id === article.categoryId) : undefined;
  const relatedArticles = article
    ? articles.filter((a) => a.categoryId === article.categoryId && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <Layout>
        <section className="pt-24 pb-16 md:pb-20 bg-background-50 min-h-[60vh]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-search-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOMeta
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(",")}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogUrl={`/article/${article.id}`}
      />
      <SchemaOrg
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.summary,
          datePublished: article.publishDate,
          url: `https://9170.com/article/${article.id}`,
          author: { "@type": "Organization", name: "9170官方" },
          publisher: { "@type": "Organization", name: "9170官方" },
        }}
      />
      <SchemaOrg
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://9170.com/" },
            { "@type": "ListItem", position: 2, name: category?.name || "分类", item: `https://9170.com/category/${article.categoryId}` },
            { "@type": "ListItem", position: 3, name: article.title, item: `https://9170.com/article/${article.id}` },
          ],
        }}
      />

      <article className="pt-24 pb-16 md:pb-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={[...getArticleBreadcrumb(article.title), { label: article.title }].filter((_, i, arr) => {
              if (arr.length > 3 && i === 1) return false;
              return true;
            })} />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${category.id}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors whitespace-nowrap"
                >
                  {category.name}
                </Link>
              )}
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-4 leading-snug" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              {article.summary}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readCount.toLocaleString()} 次阅读
              </span>
            </div>
          </header>

          <div
            className="article-content bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/60"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="text-lg font-semibold text-foreground-900 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="bg-background-100/60 rounded-lg p-4 hover:bg-background-100 transition-colors duration-200 group border border-background-200/50"
                  >
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {ra.summary}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </Layout>
  );
}